import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { isExerciseValid } from "@domain/lms/course/exercise/utils";
import { RoundedPlusButton } from "@ui/components/buttons/roundedPlusButton";
import { MaterialTextInput } from "@ui/components/inputs/materialTextInput";
import { row, stack } from "@ui/components/layout";
import { mobile } from "@ui/styles/utils";
import { useQuizEditorController } from "@ui/pages/lms/quiz/quizEditorPage/quizEditorController/quizEditorControllerContext";
import { QuizExerciseEditorWrapper } from "./quizExerciseEditorWrapper";

type QuizMainEditorProps = React.HTMLAttributes<HTMLDivElement>;
export const QuizMainEditor: React.FC<QuizMainEditorProps> = ({ ...htmlProps }) => {
	const controller = useQuizEditorController();
	const quiz = useObservable(controller.quiz);
	const [currentQuestion, setCurrentQuestion] = useState(0);
	const currentExercise = quiz.exercises.get(currentQuestion);

	useEffect(() => {
		if (!currentExercise) {
			setCurrentQuestion(0);
		}
	}, [currentExercise]);

	return (
		<Container {...htmlProps}>
			<BigTextInput
				{...htmlProps}
				placeholder={I18n.t("quiz.editor.title.placeholder") + "*"}
				value={quiz.title}
				onChange={(v) => controller.setTitle(v)}
			/>
			<QuestionsList>
				{quiz.exercises.map((ex, i) => (
					<QuestionButton
						key={ex.id}
						selected={i === currentQuestion}
						onClick={() => setCurrentQuestion(i)}
						valid={isExerciseValid(ex)}
					>
						{I18n.t("course.section.qcm.number", { index: i + 1 })}
					</QuestionButton>
				))}
				<StyledPlusButton
					hideLabel
					onClick={() => {
						controller.addExercise();
						setCurrentQuestion(quiz.exercises.size);
					}}
				/>
			</QuestionsList>
			{currentExercise && (
				<QuizExerciseEditorWrapper
					exercise={currentExercise}
					index={currentQuestion}
					deletable={quiz.exercises.size > 1}
				/>
			)}
		</Container>
	);
};

const Container = styled.div`
	${stack(40, "flex-start", "stretch")};
	@media ${mobile} {
		${stack(20, "flex-start", "stretch")};
	}
`;

const BigTextInput = styled(MaterialTextInput)`
	input,
	input::placeholder {
		${({ theme }) => theme.textStyles.title};
	}
`;

const QuestionsList = styled.div`
	${row(10, "flex-start", "stretch")};
	flex-wrap: wrap;
	> * {
		margin-bottom: 10px;
	}
`;

const QuestionButton = styled.div.withConfig({
	shouldForwardProp: (prop) => !["selected", "valid"].includes(prop),
})<{ selected?: boolean; valid: boolean }>`
	cursor: pointer;
	padding: 11px 22px;
	${({ theme }) => theme.textStyles.label};
	border-radius: 19px;
	background-color: ${({ theme, selected }) => (selected ? theme.colors.secondary : theme.colors.secondaryLight)};
	color: ${({ theme, selected }) => (selected ? theme.colors.white : theme.colors.secondary)};
	border: 1px solid ${({ theme, valid, selected }) => (valid || selected ? "transparent" : theme.colors.red)};
	${row(0, "center", "center")};
`;

const StyledPlusButton = styled(RoundedPlusButton)`
	border-radius: 20px;
	svg {
		width: 20px;
		height: 20px;
	}
`;
